import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // VENOA: Teilen alle Varianten dieselben Medien (kein eigenes Variantenbild), bleibt die
        // vorhandene Galerie unverändert stehen. Ein Austausch würde ein neues <img> erzeugen
        // (in Safari kurzes Aufblitzen, Animationen laufen erneut) und dasselbe Bild neu laden.
        if (this.#hasSameMedia(newMediaGallery, detail.resource)) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Prüft, ob die neue Galerie exakt dieselben Medien in derselben Reihenfolge zeigt
   * und die Variante kein eigenes Bild mitbringt.
   *
   * @param {Element} next - Die neu gerenderte Galerie.
   * @param {{featured_media?: {id?: number} | null} | null | undefined} variant - Die gewählte Variante.
   * @returns {boolean}
   */
  #hasSameMedia(next, variant) {
    if (variant?.featured_media?.id) return false;

    // Nur Dwells eigene Produktmedien vergleichen: VENOA legt eigene Ebenen
    // (Einzelmotiv je Duft, Vorschaubilder – [data-venoa-overlay]) in die
    // Galerie. Zählten deren Bilder mit, wäre die Galerie nie „gleich“ und
    // würde bei jedem Duftwechsel komplett neu eingesetzt (Bildflackern).
    const signature = (gallery) =>
      Array.from(gallery.querySelectorAll('img, video, model-viewer'))
        .filter((media) => !media.closest('[data-venoa-overlay]'))
        .map((media) => (media.getAttribute('src') || media.currentSrc || '').split('?')[0])
        .join('|');

    const current = signature(this);
    return current !== '' && current === signature(next);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
